import { useMemo } from "react";

import { useGetLazyPaginatedUserAlerts } from "./useGetLazyPaginatedUserAlerts";
import { AlertManagementTabsEnum } from "../../../../../common/variables/tabsSettings";
import { AutocompleteOptionType } from "../../../../../common/models/autocomplete";
import {
  filterCustomerAlerts,
  filterLocationAlerts,
  filterModelAlerts,
  IAlertsTableRow,
} from "./useAlertsTableRow";

export interface IUserAlertsTableRow {
  rowId: string;
  alertName: string;
}

export interface IUseUserAlertsTableRow {
  rows: IUserAlertsTableRow[];
  customerAlertsCount: number;
  locationAlertsCount: number;
  modelAlertsCount: number;
  loading: boolean;
}

export const useUserAlertsTableRow = (
  selectedTab?: AlertManagementTabsEnum,
  selectedLocations?: AutocompleteOptionType[] | null,
  selectedTagId?: string,
  searchValue?: string,
  selectedModel?: string
): IUseUserAlertsTableRow => {
  const { data, loading } = useGetLazyPaginatedUserAlerts();

  const rows = useMemo((): IAlertsTableRow[] => {
    try {
      return (
        data?.getUserAlerts.items?.map((item, index) => {
          const notificationSettings = JSON.parse(
            item?.notificationSettingsJson ?? "{}"
          );

          return {
            index: (index + 1).toString(),
            rowId: item?.id ?? "",
            alertName: item?.alertName ?? "",
            location: item?.location ?? "",
            model: item?.model ?? "",
            tag: item?.tag ?? "",
            alertType: item?.alertType ?? "",
            email: item?.email ?? "",
            node: item?.node ?? "",
            customService: item?.customService ?? "",
            jsonObject: item?.jsonObject,
            phoneNumber: item?.phoneNumber ?? "",
            subscribedLevelEntity: item?.subscribedLevelEntity ?? "",
            notificationSettings: {
              isEmailEnabled: notificationSettings.isEmailEnabled ?? false,
              isSMSEnabled: notificationSettings.isSMSEnabled ?? false,
              isSystemEnabled: notificationSettings.isSystemEnabled ?? false,
            },
            notificationsDiasbled: Object.values(notificationSettings).every(
              n => !n
            ),
            recipient: {
              email: item?.email ?? "",
              phoneNumber: item?.phoneNumber ?? "",
            },
            locationOrTag: {
              location: item?.location ?? "",
              tag: item?.tag ?? "",
            },
          };
        }) ?? []
      );
    } catch (error) {
      console.error(error);

      return [];
    }
  }, [data?.getUserAlerts.items]);

  const customerAlertsCount = useMemo(() => {
    return rows.filter(filterCustomerAlerts).length;
  }, [rows]);

  const locationAlertsCount = useMemo(() => {
    return rows.filter(filterLocationAlerts).length;
  }, [rows]);

  const modelAlertsCount = useMemo(() => {
    return rows.filter(filterModelAlerts).length;
  }, [rows]);

  const filteredItemsBySelectedTab = useMemo(() => {
    const filteredData = rows
      .filter(row => {
        if (!selectedTab) {
          return true;
        }

        if (selectedTab === AlertManagementTabsEnum.CUSTOMER) {
          return filterCustomerAlerts(row);
        }

        if (selectedTab === AlertManagementTabsEnum.LOCATION) {
          return filterLocationAlerts(row);
        }

        if (selectedTab === AlertManagementTabsEnum.MODEL) {
          return filterModelAlerts(row);
        }

        return true;
      })
      .map((row, index) => ({
        ...row,
        index: (index + 1).toString(),
      }));

    return filteredData;
  }, [rows, selectedTab]);

  const filteredItemsBySelectedLocations = useMemo(() => {
    if ((!selectedLocations || !selectedLocations.length) && !selectedTagId) {
      return filteredItemsBySelectedTab;
    }

    const selectedLocationsValues = filteredItemsBySelectedTab.filter(row => {
      return selectedLocations?.some(location => {
        return row.location === location?.value;
      });
    });

    const selectedTagsValues = filteredItemsBySelectedTab.filter(row => {
      return row.tag && row.tag === selectedTagId;
    });

    const selectedLocationsAndTags = [
      ...selectedLocationsValues,
      ...selectedTagsValues,
    ];

    return selectedLocationsAndTags;
  }, [filteredItemsBySelectedTab, selectedLocations, selectedTagId]);

  const filteredItemsBySearchValue = useMemo(() => {
    if (!searchValue) {
      return filteredItemsBySelectedLocations;
    }

    return filteredItemsBySelectedLocations.filter(row => {
      return row.alertName.toLowerCase().includes(searchValue.toLowerCase());
    });
  }, [filteredItemsBySelectedLocations, searchValue]);

  const filteredItemsBySelectedModel = useMemo(() => {
    if (!selectedModel) {
      return filteredItemsBySearchValue;
    }

    return filteredItemsBySearchValue.filter(row => {
      return row.model === selectedModel;
    });
  }, [filteredItemsBySearchValue, selectedModel]);

  const mappedRows = useMemo(() => {
    return filteredItemsBySelectedModel.map((row, index) => ({
      ...row,
      index: (index + 1).toString(),
    }));
  }, [filteredItemsBySelectedModel]);

  return {
    rows: mappedRows,
    customerAlertsCount,
    locationAlertsCount,
    modelAlertsCount,
    loading,
  };
};
